import { useState } from 'react';
import {
  MdAutoAwesome,
  MdDelete,
  MdDownload,
  MdUploadFile,
} from 'react-icons/md';
import { useAuthHttp } from '@hooks';
import { ImportEntityType } from '@views/MasterDataImportReview/importApi';
import { Modal } from './Modal';
import { Button } from './Button';
import { Dropzone } from './Dropzone';

export interface MasterImportModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: string;
  templateHref: string;
  templateLabel: string;
  templateCaption: string;
  uploadUrl: string;
  uploadFieldName: string;
  dropzoneId: string;
  onUploaded: () => void;
  aiImportUrl?: string;
  entityType?: ImportEntityType;
  onAiImportCreated?: (importId: string) => void;
}

export function MasterImportModal({
  isOpen,
  setIsOpen,
  title,
  templateHref,
  templateLabel,
  templateCaption,
  uploadUrl,
  uploadFieldName,
  dropzoneId,
  onUploaded,
  aiImportUrl,
  entityType,
  onAiImportCreated,
}: MasterImportModalProps) {
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const [uploadFile, setUploadFile] = useState<any>(undefined);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isAiImportLoading, setIsAiImportLoading] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadErrors, setUploadErrors] = useState<
    { message: string; row: number }[]
  >([]);

  const supportsAiImport = !!aiImportUrl && !!entityType && !!onAiImportCreated;

  const handleUploadExcel = async () => {
    try {
      setIsUploadLoading(true);
      const { data } = await authHttpFormData.post(
        uploadUrl,
        {
          [uploadFieldName]: uploadFile,
        },
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (data) {
        setUploadFile(undefined);
        setHasUploadError(false);
        setUploadErrorMessage(null);
        setIsOpen(false);
        onUploaded();
      }
    } catch (error: any) {
      const errorResponse = error as any;
      setHasUploadError(true);
      setUploadErrorMessage(null);
      setUploadErrors(errorResponse?.response?.data?.errors ?? []);
    } finally {
      setIsUploadLoading(false);
    }
  };

  const handleAiImport = async () => {
    if (!supportsAiImport) return;
    try {
      setIsAiImportLoading(true);
      const { data } = await authHttpFormData.post(
        aiImportUrl,
        {
          importFile: uploadFile,
          entityType,
        },
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (data?.importId) {
        setUploadFile(undefined);
        setHasUploadError(false);
        setUploadErrorMessage(null);
        setIsOpen(false);
        onAiImportCreated(data.importId);
      }
    } catch (error: any) {
      const errorResponse = error as any;
      setHasUploadError(true);
      setUploadErrors([]);
      setUploadErrorMessage(
        errorResponse?.response?.data?.message ??
          'The file could not be uploaded for AI review.'
      );
    } finally {
      setIsAiImportLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} title={title}>
      <div className='flex flex-col items-center justify-center gap-8 p-6 min-h-[400px]'>
        <div className='flex w-full flex-col gap-2'>
          <a href={templateHref} className='w-full' download>
            <Button
              intent='primary'
              label={templateLabel}
              size='sm'
              startIcon={<MdDownload size='1.5rem' />}
              className='w-full'
            />
          </a>
          <p className='text-center text-xs text-gray-500'>{templateCaption}</p>
        </div>
        <div className='font-semibold text-gray-700'>OR</div>
        {uploadFile ? (
          <div className='flex flex-col gap-6 w-full items-center'>
            <div className='flex flex-row gap-2 items-center'>
              <MdUploadFile size='1.5rem' />
              <span className='font-semibold text-base'>{uploadFile.name}</span>
            </div>
            {supportsAiImport ? (
              <div className='flex w-full flex-col items-center gap-1'>
                <Button
                  intent='primary'
                  label={
                    isAiImportLoading ? 'Uploading...' : 'Import with AI review'
                  }
                  startIcon={<MdAutoAwesome />}
                  onClick={handleAiImport}
                  disabled={isUploadLoading || isAiImportLoading}
                />
                <p className='text-center text-xs text-gray-500'>
                  Wingman analyses the file — you review and approve before
                  anything is saved. Works with files that don&apos;t match the
                  template.
                </p>
              </div>
            ) : (
              <Button
                intent='primary'
                label={isUploadLoading ? 'Uploading...' : 'Upload file'}
                startIcon={<MdUploadFile />}
                onClick={handleUploadExcel}
                disabled={isUploadLoading || isAiImportLoading}
              />
            )}
            <Button
              intent='danger'
              label='Clear selected file'
              startIcon={<MdDelete />}
              onClick={() => {
                setHasUploadError(false);
                setUploadErrorMessage(null);
                setUploadFile(undefined);
              }}
            />
            {hasUploadError && (
              <div className='flex flex-col gap-2 w-full'>
                <span className='text-red-600 font-semibold'>
                  Error uploading file.{' '}
                  {uploadErrors.length > 0
                    ? 'Please fix the following errors and try again.'
                    : 'Please try again.'}
                </span>
                {uploadErrorMessage && (
                  <span className='text-red-600'>{uploadErrorMessage}</span>
                )}
                {uploadErrors.map((error) => (
                  <span
                    key={`${error.row}-${error.message}`}
                    className='text-red-600'
                  >
                    Row {error.row}: {error.message}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Dropzone
            dropzoneId={dropzoneId}
            label='Upload completed template'
            onDrop={(file) => {
              setUploadFile(file);
            }}
            fileTypesTagline='.XLSX'
          />
        )}
      </div>
    </Modal>
  );
}
